import { useEffect, useMemo, useRef } from 'react';
import { trackEvent, EVENTS } from '../utils/tracking';

/**
 * Login funnel events for a phone-login modal, recorded by the modal itself so every page
 * that opens one is covered (it used to be the job page only):
 *
 *   login_prompt_shown → login_otp_sent → login_success (+ sign_up for a new account)
 *                                       ↘ login_failed (meta.reason, meta.step)
 *
 * Closing the modal without logging in — or leaving the page while it is open — is
 * login_failed with reason "dismissed" and the step it was abandoned at.
 * Never records the phone number or the code.
 *
 * @param {Object} options
 * @param {boolean} options.isOpen
 * @param {string} [options.trigger] - what asked for the login (send_cv, like, …)
 * @param {number} [options.assessmentId]
 * @param {number} [options.companyId]
 * @param {string} [options.flow] - which modal ("login_modal", "booking")
 */
export function useLoginTracking({ isOpen, trigger, assessmentId, companyId, flow = 'login_modal' }) {
  const succeededRef = useRef(false);
  const stepRef = useRef('phone');
  // Latest props for the callbacks below without re-creating them
  const contextRef = useRef({});
  contextRef.current = { trigger, assessmentId, companyId, flow };

  const tracker = useMemo(() => {
    const build = (meta = {}) => ({
      assessmentId: contextRef.current.assessmentId,
      companyId: contextRef.current.companyId,
      meta: {
        trigger: contextRef.current.trigger || null,
        flow: contextRef.current.flow,
        ...meta,
      },
    });
    return {
      build,
      /** The code was sent (or sent again) */
      otpSent: () => {
        const resend = stepRef.current === 'otp';
        stepRef.current = 'otp';
        trackEvent(EVENTS.LOGIN_OTP_SENT, build({ resend }));
      },
      /** A step failed. `detail` is the server's message, if any — never user input. */
      failed: (reason, detail) => {
        trackEvent(EVENTS.LOGIN_FAILED, build({
          reason,
          step: stepRef.current,
          // A server message could quote the number back — mask any long digit run
          detail: detail ? String(detail).replace(/\d{6,}/g, '#').slice(0, 120) : null,
        }));
      },
      succeeded: ({ isNewUser, method } = {}) => {
        succeededRef.current = true;
        trackEvent(EVENTS.LOGIN_SUCCESS, build({ isNewUser: !!isNewUser, method, step: stepRef.current }));
        if (isNewUser) trackEvent(EVENTS.SIGN_UP, build({ method }));
      },
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    succeededRef.current = false;
    stepRef.current = 'phone';
    trackEvent(EVENTS.LOGIN_PROMPT_SHOWN, tracker.build());
    return () => {
      // Runs when the modal closes or unmounts — a close without success is a dismissal
      if (!succeededRef.current) {
        trackEvent(EVENTS.LOGIN_FAILED, tracker.build({ reason: 'dismissed', step: stepRef.current }));
      }
    };
  }, [isOpen, tracker]);

  return tracker;
}

export default useLoginTracking;
